import type { RoundConditionsSnapshot, RoundSnapshot } from "./round-types.js";
import { RoundCommandError } from "./round-errors.js";

export type RoundDatePrecision = "instant" | "date" | "unknown";
export interface RoundDateValue {
  value: string | null;
  precision: RoundDatePrecision;
  source: "played-at" | "played-date" | "completed-at" | "unknown";
}

/** Calendar validation never turns a date-only fact into a stored midnight instant. */
export function isCalendarDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value))
    return false;
  const time = Date.parse(value + "T00:00:00.000Z");
  return (
    Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value
  );
}

export function validateRoundDate(
  conditions: Pick<RoundConditionsSnapshot, "playedAt" | "playedAtPrecision">,
): void {
  const { playedAt, playedAtPrecision } = conditions;
  if (playedAtPrecision === "unknown" && playedAt === null) return;
  if (playedAtPrecision === "date" && isCalendarDate(playedAt)) return;
  if (
    playedAtPrecision === undefined &&
    (playedAt === null ||
      (typeof playedAt === "string" &&
        /^\d{4}-\d{2}-\d{2}T/.test(playedAt) &&
        Number.isFinite(Date.parse(playedAt))))
  )
    return;
  throw new RoundCommandError(
    "Keep the known date precision: exact time, calendar date, or explicitly unknown.",
  );
}

/** Import arrival is never evidence of when golf was played. */
export function roundDate(
  snapshot: Pick<RoundSnapshot, "conditions" | "rules" | "schemaVersion">,
  completedAt: string | null = null,
): RoundDateValue {
  const unknown: RoundDateValue = {
    value: null,
    precision: "unknown",
    source: "unknown",
  };
  const { playedAt, playedAtPrecision } = snapshot.conditions;
  if (playedAtPrecision === "unknown") return unknown;
  if (playedAtPrecision === "date")
    return isCalendarDate(playedAt)
      ? { value: playedAt, precision: "date", source: "played-date" }
      : unknown;
  if (snapshot.rules.kind === "ggb-import" && snapshot.schemaVersion !== 3)
    return unknown;
  if (
    playedAt &&
    !isCalendarDate(playedAt) &&
    Number.isFinite(Date.parse(playedAt))
  )
    return {
      value: new Date(playedAt).toISOString(),
      precision: "instant",
      source: "played-at",
    };
  if (
    snapshot.rules.kind !== "ggb-import" &&
    completedAt &&
    Number.isFinite(Date.parse(completedAt))
  )
    return {
      value: new Date(completedAt).toISOString(),
      precision: "instant",
      source: "completed-at",
    };
  return unknown;
}
